import { useCallback, useEffect, useState } from 'react';
import { Partner, SelectorOption as OriginalSelectorOption } from 'types';
import { store } from 'redux/store';
import { getPartnersByFieldsAndQuery } from 'modules/partners/api/getPartnersByFieldsAndQuery';
import i18n from 'i18n/config';

type UseGetPartnerOptionsProps = {
  societyId: string;
  newPartnerFirst?: Partner | null;
  newPartnerSecond?: Partner | null;
  includeSharesCountFirst?: boolean;
  includeSharesCountSecond?: boolean;
  onlyActiveFirst?: boolean;
  onlyActiveSecond?: boolean;
  hasSecondPartner?: boolean;
  isSingleSelect?: boolean;
};

type HandleUpdatePartnerOptionSharesProps = {
  partnerId: string;
  sharesCount: number;
  isSelectedOptionFirst?: boolean;
  isSelectedOptionSecond?: boolean;
};

type SelectorOption = OriginalSelectorOption & {
  sharesCount?: number;
};

const fields = 'cif name sharesCount';

const getOptions = (
  partners: Partner[],
  includeSharesCount: boolean,
  onlyActive: boolean
): SelectorOption[] => {
  let partnersToMap = partners;
  if (onlyActive) {
    partnersToMap = partners.filter(
      (partner) => partner?.sharesCount?.actual > 0
    );
  }
  const options = partnersToMap.map((partner) => {
    const label = includeSharesCount
      ? `${partner?.cif} | ${partner?.name} | ${
          partner?.sharesCount?.actual
        } ${i18n.t('shares')}`
      : `${partner?.cif} | ${partner?.name}`;

    return {
      value: partner?._id,
      label,
      sharesCount: partner?.sharesCount?.actual,
    };
  });
  return options;
};

const updateOptions = (
  partnerId: string,
  prevOptions: SelectorOption[],
  sharesCount: number,
  isSelectedOption: boolean,
  setSelectedOption: (option: SelectorOption) => void // eslint-disable-line
) =>
  prevOptions.map((option) => {
    if (option.value === partnerId) {
      const prevSharesCount = +option.label.split('|')[2].split(' ')[1];

      const newOption = {
        ...option,
        label: `${option.label.split('|')[0]} | ${
          option.label.split('|')[1]
        } | ${prevSharesCount + sharesCount} ${i18n.t('shares')}`,
      };

      if (isSelectedOption) {
        setSelectedOption(newOption);
      }
      return {
        ...newOption,
      };
    }
    return option;
  });

export const useGetPartnerOptionsForOperationModal = ({
  societyId,
  newPartnerFirst,
  newPartnerSecond,
  includeSharesCountFirst = false,
  includeSharesCountSecond = false,
  onlyActiveFirst = false,
  onlyActiveSecond = false,
  isSingleSelect = false,
}: UseGetPartnerOptionsProps) => {
  const [partnerFirstOptions, setPartnerFirstOptions] = useState<
    SelectorOption[]
  >([]);
  const [partnerSecondOptions, setPartnerSecondOptions] = useState<
    SelectorOption[]
  >([]);

  const [selectedPartnerFirstOption, setSelectedPartnerFirstOption] =
    useState<SelectorOption | null>(null);
  const [selectedPartnerSecondOption, setSelectedPartnerSecondOption] =
    useState<SelectorOption | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const getFirstPartnerOptions = (partners: Partner[]) => {
    const partnerFirstOptions = getOptions(
      partners,
      includeSharesCountFirst,
      onlyActiveFirst
    );

    return partnerFirstOptions;
  };

  const getSecondPartnerOptions = (partners: Partner[]) => {
    if (isSingleSelect) {
      return [];
    }

    const partnerSecondOptions = getOptions(
      partners,
      includeSharesCountSecond,
      onlyActiveSecond
    );

    return partnerSecondOptions;
  };

  const handleChangeSelectedPartnerFirstOption = (
    selectedOption: SelectorOption | null
  ) => {
    setSelectedPartnerFirstOption(selectedOption);
  };

  const handleChangeSelectedPartnerSecondOption = (
    selectedOption: SelectorOption | null
  ) => {
    setSelectedPartnerSecondOption(selectedOption);
  };

  const handleUpdatePartnerOptionShares = ({
    partnerId,
    sharesCount,
    isSelectedOptionFirst = false,
    isSelectedOptionSecond = false,
  }: HandleUpdatePartnerOptionSharesProps) => {
    const newFirstOptions = updateOptions(
      partnerId,
      partnerFirstOptions,
      sharesCount,
      isSelectedOptionFirst,
      setSelectedPartnerFirstOption
    );

    setPartnerFirstOptions(newFirstOptions);

    if (isSingleSelect) {
      setSelectedPartnerSecondOption(null);
      return;
    }

    const newSecondOptions = updateOptions(
      partnerId,
      partnerSecondOptions,
      sharesCount,
      isSelectedOptionSecond,
      setSelectedPartnerSecondOption
    );

    setPartnerSecondOptions(newSecondOptions);
  };

  const fetchPartners = useCallback(async () => {
    if (!societyId) return;
    try {
      setIsLoading(true);

      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, { fields })
      );

      const partnerFirstOptions = getOptions(
        partners,
        includeSharesCountFirst,
        onlyActiveFirst
      );

      setPartnerFirstOptions(partnerFirstOptions);

      const partnerSecondOptions = getOptions(
        partners,
        includeSharesCountSecond,
        onlyActiveSecond
      );

      setPartnerSecondOptions(partnerSecondOptions);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [
    includeSharesCountFirst,
    includeSharesCountSecond,
    onlyActiveFirst,
    onlyActiveSecond,
    societyId,
  ]);

  const fetchPartnersByTerm = async (
    searchTerm: string,
    firstPartner?: boolean
  ) => {
    if (!searchTerm) return;
    try {
      setIsLoading(true);

      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, { fields, searchTerm })
      );

      if (firstPartner) {
        const partnerFirstOptions = getFirstPartnerOptions(partners);
        return partnerFirstOptions;
      }

      const partnerSecondOptions = getSecondPartnerOptions(partners);
      return partnerSecondOptions;
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    if (newPartnerFirst) {
      const sharesLabel = includeSharesCountFirst
        ? `| 0 ${i18n.t('shares')}`
        : '';
      setPartnerFirstOptions((prevOptions) => [
        {
          value: newPartnerFirst?._id,
          label: `${newPartnerFirst?.cif} | ${newPartnerFirst?.name} ${sharesLabel}`,
        },
        ...prevOptions,
      ]);
    }
  }, [newPartnerFirst]);

  useEffect(() => {
    if (newPartnerSecond) {
      const sharesLabel = includeSharesCountSecond
        ? `| 0 ${i18n.t('shares')}`
        : '';
      setPartnerSecondOptions((prevOptions) => [
        {
          value: newPartnerSecond?._id,
          label: `${newPartnerSecond?.cif} | ${newPartnerSecond?.name} ${sharesLabel}`,
        },
        ...prevOptions,
      ]);
    }
  }, [newPartnerSecond]);

  return {
    data: {
      selectedPartnerFirstOption,
      selectedPartnerSecondOption,
      partnerFirstOptions,
      partnerSecondOptions,
    },
    isLoading,
    isError,
    actions: {
      handleUpdatePartnerOptionShares,
      handleChangeSelectedPartnerFirstOption,
      handleChangeSelectedPartnerSecondOption,
      fetchPartnersByTerm,
    },
  };
};
